import { useState } from 'react';
import { IconSearch, IconPlus, IconCheck, IconX } from '@tabler/icons-react';
import { ActionIcon, Avatar, Badge, Box, Button, Card, Group, Select, Stack, Table, Text, TextInput, Title } from '@/components/ui';
import { mockLeaveRequests, LeaveRequest } from '../mockData/hr';

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

const getStatusColor = (status: LeaveRequest['status']) => {
  switch (status) {
    case 'approved': return 'green';
    case 'pending': return 'yellow';
    case 'rejected': return 'red';
    default: return 'gray';
  }
};

const getTypeColor = (type: LeaveRequest['type']) => {
  switch (type) {
    case 'vacation': return 'blue';
    case 'sick': return 'red';
    case 'personal': return 'gray';
    case 'parental': return 'pink';
    case 'bereavement': return 'violet';
    default: return 'gray';
  }
};

export default function LeavePage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  const statuses = ['pending', 'approved', 'rejected'];
  const types = ['vacation', 'sick', 'personal', 'parental', 'bereavement'];

  const filteredRequests = mockLeaveRequests.filter(request => {
    const matchesSearch = request.employeeName.toLowerCase().includes(search.toLowerCase()) ||
                          request.reason.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = !statusFilter || request.status === statusFilter;
    const matchesType = !typeFilter || request.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  // Sort by created date descending
  const sortedRequests = [...filteredRequests].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

  const pendingCount = mockLeaveRequests.filter(r => r.status === 'pending').length;
  const approvedCount = mockLeaveRequests.filter(r => r.status === 'approved').length;
  const rejectedCount = mockLeaveRequests.filter(r => r.status === 'rejected').length;

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Leave Management</Title>
          <Text c="dimmed" size="sm">{filteredRequests.length} requests</Text>
        </Box>
        <Button leftSection={<IconPlus size={16} />}>Request Leave</Button>
      </Group>

      {/* Summary Cards */}
      <Group>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Pending</Text>
          <Text size="xl" fw={700} c="yellow">{pendingCount}</Text>
        </Card>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Approved</Text>
          <Text size="xl" fw={700} c="green">{approvedCount}</Text>
        </Card>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Rejected</Text>
          <Text size="xl" fw={700} c="red">{rejectedCount}</Text>
        </Card>
      </Group>

      {/* Filters */}
      <Card>
        <Group>
          <TextInput
            placeholder="Search by name or reason..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Status"
            data={statuses.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
            w={150}
          />
          <Select
            placeholder="Type"
            data={types.map(t => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))}
            value={typeFilter}
            onChange={setTypeFilter}
            clearable
            w={150}
          />
        </Group>
      </Card>

      {/* Leave Requests Table */}
      <Card>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Employee</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Dates</Table.Th>
              <Table.Th>Days</Table.Th>
              <Table.Th>Reason</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th ta="right">Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortedRequests.map(request => (
              <Table.Tr key={request.id}>
                <Table.Td>
                  <Group gap="sm">
                    <Avatar size="sm" radius="xl" color="blue">
                      {request.employeeName.split(' ').map(n => n[0]).join('')}
                    </Avatar>
                    <Text size="sm">{request.employeeName}</Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" color={getTypeColor(request.type)}>{request.type}</Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{formatDate(request.startDate)} - {formatDate(request.endDate)}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{request.days}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" lineClamp={1} maw={200}>{request.reason}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" color={getStatusColor(request.status)}>{request.status}</Badge>
                </Table.Td>
                <Table.Td>
                  {request.status === 'pending' && (
                    <Group gap="xs" justify="flex-end">
                      <ActionIcon variant="light" color="green" size="sm">
                        <IconCheck size={14} />
                      </ActionIcon>
                      <ActionIcon variant="light" color="red" size="sm">
                        <IconX size={14} />
                      </ActionIcon>
                    </Group>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>
    </Stack>
  );
}
